import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Text, Card, Button, Input, Modal, EmptyState } from '@teals/ui-web';
import {
  useLazySearchSuperOrganisationsQuery,
  useLazyGetOrganisationImpactQuery,
  useDeleteOrganisationMutation,
  useWipeOrganisationMutation,
  type OrgImpact,
  type OrgSearchItem,
} from '../../store/api/staffApi';
import { typedConfirmMatches } from '../Staff/typedConfirm';

const Page = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
`;

const Banner = styled.div`
  padding: 1rem 1.25rem;
  margin: 1rem 0 1.5rem;
  background: var(--danger-subtle, #fef2f2);
  border: 1px solid var(--danger, #dc2626);
  border-radius: 10px;
  font-size: 0.875rem;
`;

const Subtitle = styled.p`
  color: var(--text-muted);
  margin: 0.25rem 0 0;
  font-size: 0.9375rem;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin: 1rem 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--bg-surface);
`;

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
`;

const ImpactCard = styled(Card)`
  padding: 0.85rem 1rem;
`;

const SuperUser: React.FC = () => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<OrgSearchItem[]>([]);
  const [selected, setSelected] = useState<OrgSearchItem | null>(null);
  const [impact, setImpact] = useState<OrgImpact | null>(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState<'soft' | 'hard'>('soft');
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [hasSearched, setHasSearched] = useState(false);
  const [searchOrgs, { isFetching: searching }] =
    useLazySearchSuperOrganisationsQuery();
  const [loadImpact, { isFetching: loadingImpact }] =
    useLazyGetOrganisationImpactQuery();
  const [deleteOrg, { isLoading: deleting }] = useDeleteOrganisationMutation();
  const [wipeOrg, { isLoading: wiping }] = useWipeOrganisationMutation();
  const busy = deleting || wiping;

  const handleSearch = useCallback(async () => {
    setErrorMsg(null);
    setSuccessMsg(null);
    setSelected(null);
    setImpact(null);
    setHasSearched(true);
    try {
      const data = await searchOrgs({ q }).unwrap();
      setResults(data.items || []);
    } catch {
      setErrorMsg('Search failed. Ensure you have the super_user Cognito group.');
      setResults([]);
    }
  }, [q, searchOrgs]);

  // Mirror Staff Pricing: empty query lists active orgs (limit 50).
  useEffect(() => {
    void handleSearch();
    // Intentionally once on mount — later searches are explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only list
  }, []);

  const handleSelect = async (org: OrgSearchItem) => {
    setSelected(org);
    setConfirmName('');
    setSuccessMsg(null);
    setErrorMsg(null);
    try {
      const data = await loadImpact(org.orgId).unwrap();
      setImpact(data);
    } catch {
      setErrorMsg('Could not load organisation impact summary.');
      setImpact(null);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setErrorMsg(null);
    try {
      const payload = { orgId: selected.orgId, confirmName };
      const result =
        deleteMode === 'hard'
          ? await wipeOrg(payload).unwrap()
          : await deleteOrg(payload).unwrap();
      setSuccessMsg(result.message || `Deleted ${result.name}`);
      setConfirmOpen(false);
      setSelected(null);
      setImpact(null);
      setConfirmName('');
      setResults((prev) => prev.filter((o) => o.orgId !== result.orgId));
    } catch (err: unknown) {
      const data = (err as { data?: { message?: string } })?.data;
      setErrorMsg(data?.message || 'Delete failed. Name must match exactly.');
    }
  };

  const nameMatches = typedConfirmMatches(confirmName, selected?.name || '');
  const cognitoWipe = (impact?.cognitoUsersToDelete || []).map((person) => person.email);
  const retained = (impact?.usersRetained || []).map((person) => person.email);

  return (
    <Page>
      <Text variant="h2">Super User</Text>
      <Subtitle>Irreversible platform actions.</Subtitle>
      <Banner>
        <strong>Super User · irreversible platform actions</strong>
        <div style={{ marginTop: '0.35rem' }}>
          Soft delete dissolves the tenant and keeps data. Hard wipe deletes
          tenant data and Cognito users who belong only to that organisation.
          Stripe customers stay in Stripe.
        </div>
      </Banner>

      {successMsg && (
        <Card style={{ padding: '1rem', marginBottom: '1rem' }}>
          <Text variant="body1">{successMsg}</Text>
        </Card>
      )}
      {errorMsg && (
        <Card style={{ padding: '1rem', marginBottom: '1rem' }}>
          <Text variant="body2">{errorMsg}</Text>
        </Card>
      )}

      <Card style={{ padding: '1.25rem' }}>
        <Text variant="h4">Delete organisation</Text>
        <SearchRow>
          <Input
            style={{ flex: 1, minWidth: 200 }}
            placeholder="Filter by name or id (blank = all)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleSearch();
            }}
          />
          <Button onClick={() => void handleSearch()} disabled={searching}>
            Search
          </Button>
        </SearchRow>

        <List>
          {results.map((org) => (
            <Row key={org.orgId}>
              <div>
                <div>{org.name}</div>
                <Subtitle>
                  {org.orgId} · {org.memberCount} members · {org.status}
                </Subtitle>
              </div>
              <Button
                size="sm"
                variant={selected?.orgId === org.orgId ? 'primary' : 'secondary'}
                onClick={() => void handleSelect(org)}
              >
                Select
              </Button>
            </Row>
          ))}
          {!results.length && !searching && (
            <EmptyState
              title={
                hasSearched
                  ? 'No organisations found'
                  : 'Search for an organisation'
              }
              description={
                hasSearched
                  ? 'Try a different name or id. Dissolved orgs are excluded.'
                  : 'Leave blank and search to list active organisations, or filter by name/id.'
              }
            />
          )}
          {searching && !results.length && (
            <Text variant="body2">Loading organisations…</Text>
          )}
        </List>
      </Card>

      {selected && (
        <Card style={{ padding: '1.25rem', marginTop: '1.25rem' }}>
          <Text variant="h4">Impact — {selected.name}</Text>
          {loadingImpact && <Text variant="body2">Loading impact…</Text>}
          {impact && (
            <>
              <ImpactGrid>
                <ImpactCard>
                  <Subtitle>Members</Subtitle>
                  <Text variant="h3">{impact.memberCount}</Text>
                </ImpactCard>
                <ImpactCard>
                  <Subtitle>Applications</Subtitle>
                  <Text variant="h3">{impact.applicationsCount}</Text>
                </ImpactCard>
                <ImpactCard>
                  <Subtitle>Agents</Subtitle>
                  <Text variant="h3">{impact.agentsCount}</Text>
                </ImpactCard>
                <ImpactCard>
                  <Subtitle>Billing profile</Subtitle>
                  <Text variant="h3">
                    {impact.billingProfilePresent ? 'Yes' : 'No'}
                  </Text>
                </ImpactCard>
              </ImpactGrid>
              <Text variant="body2">
                Hard wipe removes from Cognito:{' '}
                {cognitoWipe.length ? cognitoWipe.join(', ') : 'none'}
              </Text>
              <Text variant="body2">
                These people only lose this membership:{' '}
                {retained.length ? retained.join(', ') : 'none'}
              </Text>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDeleteMode('soft');
                    setConfirmName('');
                    setConfirmOpen(true);
                  }}
                  disabled={impact.status === 'dissolved'}
                >
                  Soft delete…
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    setDeleteMode('hard');
                    setConfirmName('');
                    setConfirmOpen(true);
                  }}
                >
                  Hard wipe…
                </Button>
              </div>
            </>
          )}
        </Card>
      )}

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={deleteMode === 'hard' ? 'Confirm hard wipe' : 'Confirm soft delete'}
      >
        <Text variant="body2">
          {deleteMode === 'hard'
            ? `Hard wipe deletes tenant data for ${selected?.name} and Cognito users who belong only to it. Stripe customers stay in Stripe. Type the organisation name exactly.`
            : `Soft delete dissolves ${selected?.name} and keeps data. Type the organisation name exactly.`}
        </Text>
        <Input
          style={{ marginTop: '1rem' }}
          placeholder="Organisation name"
          value={confirmName}
          onChange={(e) => setConfirmName(e.target.value)}
        />
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <Button
            variant="danger"
            disabled={!nameMatches || busy}
            onClick={() => void handleDelete()}
          >
            {busy
              ? 'Deleting…'
              : deleteMode === 'hard'
                ? 'Wipe permanently'
                : 'Soft delete'}
          </Button>
          <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
        </div>
      </Modal>
    </Page>
  );
};

export default SuperUser;
